import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Calendar, TrendingUp, Sparkles, UserPlus, Megaphone, Video, ChevronDown, ChevronUp, Heart, MessageCircle, ArrowRight, Users, Pencil, Pin, Newspaper, ExternalLink, MessagesSquare, Vote, Plus, Globe, Lock } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";
import { getVisitorId } from "../../lib/utils";
import PremiumBenefitsModal from "../PremiumBenefitsModal";
import { canViewMembers } from "../MembershipGuardModal";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// 뉴스 게시일 포맷: ISO → "2026.08.05"
function fmtNewsDate(s) {
  if (!s) return "";
  const d = new Date(s);
  if (isNaN(d)) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}.${m}.${day}`;
}

/**
 * 중년싱글 뉴스 & 트렌드 — 커뮤니티 첫 화면 컴팩트 리스트 (이미지/요약 없음)
 * 각 행: 카테고리 | 제목 | 매체명 | 날짜 (모바일=2줄), 행 전체 클릭 → 원문 새 창
 */
function CommunityNews() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetch(`${API_URL}/api/news/feed?limit=3`)
      .then((r) => (r.ok ? r.json() : { articles: [] }))
      .then((d) => setArticles(d.articles || []))
      .catch(() => setArticles([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="rounded-2xl bg-white border border-stone-200 overflow-hidden" data-testid="rail-news-section">
      <div className="flex items-center justify-between px-4 pt-3.5 pb-1">
        <div className="flex items-center gap-1.5">
          <Newspaper className="w-4 h-4 text-burgundy-600" strokeWidth={2.2} />
          <h3 className="font-serif text-[15px] text-stone-800">중년싱글 뉴스 &amp; 트렌드</h3>
        </div>
        <button
          onClick={() => navigate("/news")}
          className="text-[12px] font-semibold text-burgundy-600 hover:text-burgundy-700 whitespace-nowrap"
          data-testid="rail-news-viewall"
        >
          전체보기 →
        </button>
      </div>
      <p className="px-4 pb-2 text-[11.5px] leading-snug animate-ai-text-shine">
        국내외 미디어가 주목한 중년 싱글의 사랑과 새로운 삶
      </p>
      <div className="divide-y divide-stone-100 border-t border-stone-100">
        {loading ? (
          <div className="px-4 py-4 text-xs text-stone-400 text-center">불러오는 중…</div>
        ) : articles.length === 0 ? (
          <div className="px-4 py-4 text-xs text-stone-400 text-center">등록된 기사가 곧 올라올 예정이에요</div>
        ) : (
          articles.map((a, i) => (
            <a
              key={a.id}
              href={a.url}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex flex-col gap-0.5 px-4 py-2.5 hover:bg-[#faf7f2] transition-colors"
              data-testid={`rail-news-item-${i}`}
            >
              <div className="flex items-center gap-1.5 min-w-0">
                {(a.is_featured || a.is_pinned) && i === 0 && (
                  <span className="shrink-0 text-[10px] font-bold text-white bg-burgundy-600 rounded px-1.5 py-0.5">주목</span>
                )}
                <span className="shrink-0 text-[11px] font-semibold text-stone-500">[{a.category}]</span>
                <span className="min-w-0 truncate text-[10px] font-normal text-stone-500 group-hover:text-burgundy-600 group-hover:underline">
                  {a.korean_title || a.original_title}
                </span>
              </div>
              <div className="flex items-center gap-1.5 text-[11px] text-stone-400">
                <span className="truncate">{a.source_name}</span>
                <span>·</span>
                <span className="whitespace-nowrap">{fmtNewsDate(a.published_date)}</span>
                {(a.likes > 0 || a.comment_count > 0) && (
                  <span className="flex items-center gap-1.5 whitespace-nowrap text-stone-400">
                    <span className="flex items-center gap-0.5"><Heart className="w-3 h-3" />{a.likes || 0}</span>
                    <span className="flex items-center gap-0.5"><MessageCircle className="w-3 h-3" />{a.comment_count || 0}</span>
                  </span>
                )}
                <ExternalLink className="w-3 h-3 ml-auto text-stone-300 group-hover:text-burgundy-500" />
              </div>
            </a>
          ))
        )}
      </div>
    </div>
  );
}

// 커플 발표일 포맷: "2026-06-06" → "2026.6.6"
function fmtCoupleDate(s) {
  if (!s) return "";
  const m = String(s).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[1]}.${parseInt(m[2], 10)}.${parseInt(m[3], 10)}`;
  return s;
}

// 가입 경과 시간 (예: 방금 전 / 3시간 전 / 1일 전)
function joinedAgo(iso) {
  if (!iso) return "신규";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "방금 전";
  if (diff < 3600) return `${Math.floor(diff / 60)}분 전`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}시간 전`;
  if (diff < 86400 * 30) return `${Math.floor(diff / 86400)}일 전`;
  return new Date(iso).toLocaleDateString("ko-KR", { timeZone: "Asia/Seoul" });
}

// 성별에 따른 아바타 색상 (여성=핑크, 남성=블루) — 톤을 맞춘 연한 배경 + 진한 글자
function genderAvatarClass(gender) {
  const g = (gender || "").toString().trim().toLowerCase();
  const isMale = ["male", "m", "남성", "남자", "man"].includes(g);
  const isFemale = ["female", "f", "여성", "여자", "woman"].includes(g);
  if (isMale) return "bg-sky-100 text-sky-500";
  if (isFemale) return "bg-pink-100 text-pink-500";
  return "bg-stone-100 text-stone-400"; // 미지정
}

// 성별 한글 라벨
function genderLabel(gender) {
  const g = (gender || "").toString().trim().toLowerCase();
  if (["male", "m", "남성", "남자", "man"].includes(g)) return "남성";
  if (["female", "f", "여성", "여자", "woman"].includes(g)) return "여성";
  return "";
}

// 출생년도 → 나이 (한국식 세는나이 근사)
function ageFromYear(year) {
  const y = parseInt(year);
  if (!y || y < 1900) return "";
  return `${new Date().getFullYear() - y + 1}세`;
}

function Section({ icon: Icon, title, action, children, testid }) {
  return (
    <section className="bg-white rounded-2xl border border-stone-100 p-4 shadow-[0_1px_3px_rgba(0,0,0,0.02)]" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="flex items-center gap-2 font-serif text-[15px] text-stone-900">
          <Icon className="w-4 h-4 text-burgundy-400" strokeWidth={1.8} />
          {title}
        </h3>
        {action}
      </div>
      {children}
    </section>
  );
}

function AdBanner() {
  const [ads, setAds] = useState(null); // null = loading, [] = empty fallback
  const [start, setStart] = useState(0);

  useEffect(() => {
    fetch(`${API_URL}/api/recommended-ads`)
      .then((r) => (r.ok ? r.json() : { ads: [] }))
      .then((d) => setAds(Array.isArray(d.ads) ? d.ads : []))
      .catch(() => setAds([]));
  }, []);

  // 색상 테마 매핑 (Tailwind safelist 친화적으로 명시적 매핑)
  const COLOR_MAP = {
    blue:     { tag: "text-blue-500",     title: "text-blue-700" },
    emerald:  { tag: "text-emerald-500",  title: "text-emerald-700" },
    amber:    { tag: "text-amber-600",    title: "text-amber-800" },
    rose:     { tag: "text-rose-500",     title: "text-rose-700" },
    violet:   { tag: "text-violet-500",   title: "text-violet-700" },
    burgundy: { tag: "text-burgundy-500", title: "text-burgundy-700" },
    stone:    { tag: "text-stone-500",    title: "text-stone-800" },
    sky:      { tag: "text-sky-500",      title: "text-sky-700" },
    teal:     { tag: "text-teal-500",     title: "text-teal-700" },
  };

  // 기본 광고 (DB에 등록된 광고가 없을 때 폴백)
  const fallbackAds = [
    { id: "default-1", tag: "여행",     title: "프리미엄 패키지 30% 할인", color_theme: "blue",    link_url: "" },
    { id: "default-2", tag: "건강식품", title: "갱년기 영양제 신제품",     color_theme: "emerald", link_url: "" },
    { id: "default-3", tag: "골프",     title: "회원제 골프장 멤버십",     color_theme: "amber",   link_url: "" },
  ];

  const list = ads === null ? null : (ads.length > 0 ? ads : fallbackAds);
  const VISIBLE = 3;
  const rotating = !!list && list.length > VISIBLE;

  // 자동 순환: 3.5초마다 1칸씩 회전
  useEffect(() => {
    if (!rotating) return;
    const t = setInterval(() => setStart((s) => (s + 1) % list.length), 3500);
    return () => clearInterval(t);
  }, [rotating, list ? list.length : 0]);

  const visible = !list
    ? null
    : rotating
      ? Array.from({ length: VISIBLE }, (_, i) => list[(start + i) % list.length])
      : list;

  const renderAd = (a) => {
    const colors = COLOR_MAP[a.color_theme] || COLOR_MAP.blue;
    const inner = (
      <>
        <div className={`text-[10px] font-bold tracking-wider uppercase mb-1 ${colors.tag}`}>{a.tag}</div>
        <div className={`text-sm font-semibold ${colors.title}`}>{a.title}</div>
        <div className="text-[11px] text-stone-400 mt-1">스폰서 · 자세히 보기</div>
      </>
    );
    const cls = "block rounded-xl p-3 bg-white border border-stone-100 hover:border-stone-200 transition-colors";
    return a.link_url ? (
      <a key={a.id} href={a.link_url} target="_blank" rel="noopener noreferrer" className={cls} data-testid={`rail-ad-${a.id}`}>
        {inner}
      </a>
    ) : (
      <div key={a.id} className={cls} data-testid={`rail-ad-${a.id}`}>
        {inner}
      </div>
    );
  };

  return (
    <Section icon={Megaphone} title="추천 광고" testid="rail-ads">
      {visible === null ? (
        <div className="text-xs text-stone-400 py-2 text-center">불러오는 중…</div>
      ) : rotating ? (
        <div className="relative overflow-hidden">
          <AnimatePresence mode="wait">
            <motion.div
              key={start}
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -14 }}
              transition={{ duration: 0.5, ease: "easeInOut" }}
              className="space-y-2"
            >
              {visible.map((a, i) => renderAd({ ...a, id: `${a.id}-${i}` }))}
            </motion.div>
          </AnimatePresence>
        </div>
      ) : (
        <div className="space-y-2">{visible.map(renderAd)}</div>
      )}
    </Section>
  );
}

function CoupleHighlight() {
  const [couple, setCouple] = useState(null);
  const [loading, setLoading] = useState(true);
  const [likes, setLikes] = useState(0);
  const [liked, setLiked] = useState(false);
  const [commentCount, setCommentCount] = useState(0);
  const [liking, setLiking] = useState(false);
  const navigate = useNavigate();
  const { token } = useAuth();

  useEffect(() => {
    fetch(`${API_URL}/api/liz-couples/latest`)
      .then((r) => (r.ok ? r.json() : { couple: null }))
      .then((d) => {
        const c = d.couple || null;
        setCouple(c);
        if (c) {
          const vid = getVisitorId();
          fetch(`${API_URL}/api/liz-couples/${c.id}/social?visitor_id=${encodeURIComponent(vid)}`, {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          })
            .then((r) => (r.ok ? r.json() : null))
            .then((s) => {
              if (s) { setLikes(s.likes || 0); setLiked(!!s.liked); setCommentCount(s.comment_count || 0); }
            })
            .catch(() => {});
        }
      })
      .finally(() => setLoading(false));
  }, [token]);

  const toggleLike = async (e) => {
    e.stopPropagation();
    if (!couple || liking) return;
    setLiking(true);
    try {
      const r = await fetch(`${API_URL}/api/liz-couples/${couple.id}/like`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ visitor_id: getVisitorId() }),
      });
      if (r.ok) { const d = await r.json(); setLikes(d.likes); setLiked(d.liked); }
    } catch { /* noop */ }
    setLiking(false);
  };

  if (loading) {
    return (
      <Section icon={Heart} title="우리 커플됐어요" testid="rail-couple-highlight">
        <div className="text-xs text-stone-400 py-2 text-center">불러오는 중…</div>
      </Section>
    );
  }
  if (!couple) {
    return (
      <Section icon={Heart} title="우리 커플됐어요" testid="rail-couple-highlight">
        <div className="text-xs text-stone-400 py-2 text-center">곧 행복한 소식을 전해드릴게요 💕</div>
      </Section>
    );
  }
  return (
    <Section icon={Heart} title="우리 커플됐어요" testid="rail-couple-highlight">
      <button
        onClick={() => navigate(`/couples/${couple.id}`)}
        className="w-full text-left group"
        data-testid="rail-couple-card"
      >
        <div className="relative rounded-xl overflow-hidden aspect-[4/3] bg-gradient-to-br from-stone-100 to-stone-200">
          {couple.photo_url ? (
            <img src={couple.photo_url} alt={`${couple.couple_no}호 커플`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" style={{ objectPosition: couple.photo_position === "top" ? "center top" : couple.photo_position === "bottom" ? "center bottom" : "center" }} />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Heart className="w-10 h-10 text-stone-300 fill-stone-200" />
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-transparent" />
          <span className="absolute top-2 left-2 bg-burgundy-700 text-white text-[11px] font-bold px-2.5 py-1 rounded-full shadow">
            {couple.couple_no}호
          </span>
          {couple.announced_date && (
            <span className="absolute top-2 right-2 bg-white/90 text-burgundy-700 text-[10.5px] font-bold px-2 py-1 rounded-full shadow" data-testid="rail-couple-date">
              {fmtCoupleDate(couple.announced_date)}
            </span>
          )}
          <div className="absolute bottom-2 left-3 right-3">
            <p className="text-white font-bold text-base drop-shadow">{couple.couple_name || `${couple.couple_no}호 커플`}</p>
          </div>
        </div>
      </button>

      {/* 좋아요 / 댓글 */}
      <div className="mt-2 flex items-center gap-3 px-0.5">
        <button
          onClick={toggleLike}
          disabled={liking}
          className={`flex items-center gap-1 text-sm font-semibold transition-colors ${liked ? "text-rose-500" : "text-stone-400 hover:text-rose-400"}`}
          data-testid="rail-couple-like-btn"
        >
          <Heart className={`w-4 h-4 ${liked ? "fill-rose-500" : ""}`} />
          <span data-testid="rail-couple-like-count">{likes}</span>
        </button>
        <button
          onClick={() => navigate(`/couples/${couple.id}`)}
          className="flex items-center gap-1 text-sm font-semibold text-stone-400 hover:text-burgundy-500 transition-colors"
          data-testid="rail-couple-comment-btn"
        >
          <MessageCircle className="w-4 h-4" />
          <span data-testid="rail-couple-comment-count">{commentCount}</span>
        </button>
      </div>

      <button
        onClick={() => navigate(`/couples/${couple.id}`)}
        className="mt-2 w-full flex items-center justify-center gap-1.5 py-2 rounded-xl bg-stone-100 hover:bg-burgundy-50 text-burgundy-700 text-sm font-semibold transition-colors"
        data-testid="rail-couple-story-btn"
      >
        커플 스토리 보기 <ChevronDown className="w-4 h-4 -rotate-90" />
      </button>
    </Section>
  );
}

function MemberCircleAvatar({ src, name, gender }) {
  const [failed, setFailed] = useState(false);
  if (src && !failed) {
    return (
      <img
        src={src}
        alt=""
        onError={() => setFailed(true)}
        className="w-9 h-9 rounded-full object-cover border border-stone-100"
      />
    );
  }
  return (
    <div className={`w-9 h-9 rounded-full ${genderAvatarClass(gender)} flex items-center justify-center font-bold text-sm`}>
      {(name || "?").charAt(0)}
    </div>
  );
}

function RecommendedMembers() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  useEffect(() => {
    fetch(`${API_URL}/api/community/new-members`)
      .then((r) => r.ok ? r.json() : { members: [] })
      .then((d) => setMembers(Array.isArray(d.members) ? d.members : []))
      .finally(() => setLoading(false));
  }, []);

  const visible = members.slice(0, expanded ? 12 : 4);
  const canExpand = members.length > 4;

  return (
    <Section
      icon={Sparkles}
      title="신규 가입 회원"
      testid="rail-new-members"
    >
      {loading ? (
        <div className="text-xs text-stone-400 py-2 text-center">불러오는 중…</div>
      ) : members.length === 0 ? (
        <div className="text-xs text-stone-400 py-2 text-center">신규 회원이 없어요.</div>
      ) : (
        <ul className="space-y-2.5">
          {visible.map((m) => (
            <li key={m.user_id} className="flex items-center gap-2.5">
              <Link to={`/timeline/${m.user_id}`} className="flex items-center gap-2.5 flex-1 min-w-0 group">
                {m.profile_image ? (
                  <MemberCircleAvatar
                    src={m.profile_image.startsWith("http") ? m.profile_image : `${API_URL}${m.profile_image}`}
                    name={m.name}
                    gender={m.gender}
                  />
                ) : (
                  <div className={`w-9 h-9 rounded-full ${genderAvatarClass(m.gender)} flex items-center justify-center font-bold text-sm`}>
                    {(m.name || "?").charAt(0)}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-medium text-stone-900 truncate group-hover:text-burgundy-700">{m.name}</div>
                  <div className="text-[11px] text-stone-400 truncate">
                    {m.location ? <span>{m.location} · </span> : null}
                    {genderLabel(m.gender) ? (
                      <span className={`font-bold ${["male","m","남성","남자","man"].includes((m.gender||"").toString().trim().toLowerCase()) ? "text-amber-500" : "text-burgundy-600"}`}>
                        {genderLabel(m.gender)}
                      </span>
                    ) : null}
                    {ageFromYear(m.birth_year) ? <span> · {ageFromYear(m.birth_year)}</span> : null}
                    {!m.location && !genderLabel(m.gender) && !ageFromYear(m.birth_year) ? "신규 가입" : null}
                  </div>
                </div>
              </Link>
              <span className="text-[10px] text-stone-400 font-medium shrink-0" data-testid={`new-member-joined-${m.user_id}`}>{joinedAgo(m.joined_at)}</span>
            </li>
          ))}
        </ul>
      )}
      {!loading && canExpand && (
        <button
          onClick={() => setExpanded((v) => !v)}
          className="mt-3 w-full py-2 text-center rounded-lg bg-stone-50 hover:bg-burgundy-50 border border-stone-200 hover:border-burgundy-200 text-[12px] font-medium text-stone-700 hover:text-burgundy-700 transition-colors"
          data-testid="rail-new-members-more-btn"
        >
          {expanded ? "접기" : "신규 회원 더보기 →"}
        </button>
      )}
    </Section>
  );
}

function UpcomingEvents() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user, token } = useAuth();
  const isAdmin = !!(user && (user.role === "admin" || user.is_admin || user.email === "admin@newcoupling.com"));
  const navigate = useNavigate();

  const loadEvents = () => {
    // 공개 이벤트 API 사용 (인증 불필요)
    fetch(`${API_URL}/api/events?limit=50`)
      .then((r) => r.ok ? r.json() : { events: [] })
      .then((d) => {
        const list = Array.isArray(d) ? d : (d.events || []);
        // 지난 이벤트 자동 필터링 (오늘 자정 기준) — 단, 고정(pinned)된 이벤트는 항상 노출
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const upcoming = list.filter((e) => {
          if (e.pinned) return true;
          const raw = e.date || e.event_date;
          if (!raw) return false;
          try { return new Date(raw) >= today; } catch { return false; }
        });
        // 고정 이벤트 먼저, 그 다음 가까운 날짜 순
        upcoming.sort((a, b) => {
          const pa = a.pinned ? 1 : 0, pb = b.pinned ? 1 : 0;
          if (pa !== pb) return pb - pa;
          const da = new Date(a.date || a.event_date).getTime();
          const dbb = new Date(b.date || b.event_date).getTime();
          return da - dbb;
        });
        setEvents(upcoming.slice(0, 10));
      })
      .catch(() => setEvents([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadEvents(); }, []);

  const togglePin = async (e, ev) => {
    e.stopPropagation();
    try {
      const res = await fetch(`${API_URL}/api/admin/events/${ev.id}/pin`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ pinned: !ev.pinned }),
      });
      if (res.ok) loadEvents();
    } catch { /* silent */ }
  };

  return (
    <Section icon={Calendar} title="다가오는 오프라인 이벤트" testid="rail-upcoming-events">
      <p className="text-[11px] text-stone-400 mb-2">모든 날짜·시간은 한국 시간(KST) 기준입니다.</p>
      {loading ? (
        <div className="text-xs text-stone-400 py-2 text-center">불러오는 중…</div>
      ) : events.length === 0 ? (
        <div className="text-xs text-stone-400 py-2 text-center">예정된 이벤트가 없어요.</div>
      ) : (
        <>
          <ul className="space-y-2.5">
            {events.slice(0, 2).map((ev) => {
              const evDate = ev.date || ev.event_date;
              const img = ev.image_url || ev.image;
              return (
                <li key={ev.id || ev.title} className="relative">
                  <button
                    onClick={() => navigate(`/events`)}
                    className="w-full text-left flex gap-3 group"
                    data-testid={`rail-event-${ev.id}`}
                  >
                    {img ? (
                      <img
                        src={img.startsWith("http") ? img : `${API_URL}${img}`}
                        alt=""
                        className="w-24 h-24 rounded-xl object-cover shrink-0"
                      />
                    ) : (
                      <div className="w-24 h-24 rounded-xl bg-gradient-to-br from-amber-100 to-rose-100 flex items-center justify-center shrink-0">
                        <Calendar className="w-9 h-9 text-burgundy-600" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] font-medium text-stone-900 line-clamp-2 group-hover:text-burgundy-700">{ev.title}</div>
                      {ev.eligibility && ev.eligibility !== "all" && (
                        <span className="inline-block mt-1 px-2 py-0.5 bg-burgundy-600 text-white text-[10px] font-bold rounded-full" data-testid="rail-event-eligibility-badge">
                          {ev.eligibility === "vip" ? "VIP 전용" : "정회원 전용"}
                        </span>
                      )}
                      <div className="text-[11px] text-stone-400 mt-0.5">
                        {evDate ? new Date(evDate).toLocaleDateString("ko-KR", { year: "numeric", month: "long", day: "numeric", timeZone: "Asia/Seoul" }) : ""}
                      </div>
                    </div>
                  </button>
                  {isAdmin && (
                    <button
                      onClick={(e) => togglePin(e, ev)}
                      title={ev.pinned ? "고정 해제" : "상단 고정"}
                      className="absolute bottom-0.5 right-0.5 p-1 text-stone-400 hover:text-stone-600 transition-colors z-10"
                      data-testid={`rail-event-pin-btn-${ev.id}`}
                    >
                      <Pin className={`w-3 h-3 ${ev.pinned ? "fill-current text-stone-600" : ""}`} />
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
          <button
            onClick={() => navigate("/events")}
            className="mt-3 w-full py-2 text-center rounded-lg bg-stone-50 hover:bg-burgundy-50 border border-stone-200 hover:border-burgundy-200 text-[12px] font-medium text-stone-700 hover:text-burgundy-700 transition-colors"
            data-testid="rail-events-view-all-btn"
          >
            전체 이벤트 보기 →
          </button>
        </>
      )}
    </Section>
  );
}

function WorryRoomCard() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);

  useEffect(() => {
    let alive = true;
    fetch(`${API_URL}/api/worries`)
      .then((r) => (r.ok ? r.json() : { real: [], examples: [] }))
      .then((d) => {
        if (!alive) return;
        const merged = [...(d.real || []), ...(d.examples || [])].slice(0, 3);
        setItems(merged);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  return (
    <Section icon={MessagesSquare} title="익명 고민방" testid="rail-worry-room"
      action={
        <button onClick={() => navigate("/worries")} className="text-[12px] font-semibold text-burgundy-600 hover:text-burgundy-700 whitespace-nowrap" data-testid="rail-worry-viewall">
          전체보기 →
        </button>
      }
    >
      <p className="text-[11.5px] text-stone-500 leading-snug mb-2.5">혼자 고민하지 마세요. 우리가 함께 생각해 드립니다.</p>
      <div className="space-y-2">
        {items.map((w) => (
          <button
            key={w.id}
            onClick={() => navigate(`/worries?w=${w.id}`)}
            className="w-full text-left rounded-xl border border-stone-100 bg-stone-50/60 hover:bg-burgundy-50/50 hover:border-burgundy-100 px-3 py-2.5 transition-colors"
            data-testid={`rail-worry-item-${w.id}`}
          >
            <div className="flex items-center gap-1.5 mb-1">
              {w.is_ai_seed ? (
                <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-burgundy-50 text-burgundy-600 text-[9.5px] font-bold border border-burgundy-100"><Sparkles className="w-2.5 h-2.5" />AI 예시</span>
              ) : (w.category ? <span className="px-1.5 py-0.5 rounded-full bg-stone-100 text-stone-500 text-[9.5px] font-semibold">{w.category}</span> : null)}
            </div>
            <div className="text-[12.5px] font-semibold text-stone-800 leading-snug line-clamp-2">{w.title}</div>
            <div className="flex items-center gap-3 mt-1.5 text-[10.5px] text-stone-400">
              <span className="inline-flex items-center gap-0.5"><Vote className="w-3 h-3" />{w.vote_total}</span>
              <span className="inline-flex items-center gap-0.5"><MessageCircle className="w-3 h-3" />{w.comment_count}</span>
              <span className="inline-flex items-center gap-0.5"><Heart className="w-3 h-3" />{w.empathy_count}</span>
            </div>
          </button>
        ))}
      </div>
      <button
        onClick={() => navigate("/worries")}
        className="mt-3 w-full inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-full bg-burgundy-600 hover:bg-burgundy-700 text-white text-[13px] font-semibold transition-colors"
        data-testid="rail-worry-create-btn"
      >
        <Plus className="w-4 h-4" strokeWidth={2.2} />
        나도 익명으로 고민 올리기
      </button>
    </Section>
  );
}

// 정회원 전용 공간 — 정회원 = 싱글임이 서류로 검증된 유료회원
function PremiumZoomMembersCard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [showBenefits, setShowBenefits] = useState(false);
  const [thumbs, setThumbs] = useState([]);
  const isMember = canViewMembers(user);

  useEffect(() => {
    let alive = true;
    fetch(`${API_URL}/api/community/new-members`)
      .then((r) => (r.ok ? r.json() : { members: [] }))
      .then((d) => { if (alive) setThumbs((Array.isArray(d.members) ? d.members : []).slice(0, 4)); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const thumbUrl = (m) => (m.profile_image ? (m.profile_image.startsWith("http") ? m.profile_image : `${API_URL}${m.profile_image}`) : null);

  return (
    <section
      className="rounded-2xl border border-sky-100 bg-gradient-to-br from-pink-50 via-white to-sky-50 p-4 shadow-[0_2px_12px_-4px_rgba(147,197,253,0.3)]"
      data-testid="rail-premium-card"
    >
      {/* ① 정회원 전용 공간 (최우선 메시지) */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-gradient-to-br from-pink-200 to-sky-200 text-sky-600">
          <Lock className="w-4 h-4" strokeWidth={2.4} />
        </span>
        <h3 className="font-serif text-[16.5px] font-bold text-stone-800" data-testid="rail-premium-title">정회원 전용 공간</h3>
        <span className="ml-auto inline-flex items-center gap-0.5 text-[9.5px] font-bold tracking-wide text-sky-600 bg-sky-50 px-2 py-0.5 rounded-full border border-sky-100">★ PREMIUM</span>
      </div>
      <p className="text-[12.5px] text-stone-600 leading-relaxed mt-1.5" data-testid="rail-premium-subtitle">
        <span className="font-semibold text-stone-800">싱글임이 서류로 검증된 유료회원</span>만 이용할 수 있습니다.
      </p>

      {/* ② 혜택 1 — 글로벌 줌미팅 */}
      <div className="mt-3.5 rounded-2xl border border-sky-100 bg-gradient-to-br from-sky-50/70 to-pink-50/50 p-3.5" data-testid="rail-zoom-block">
        <div className="flex items-start gap-2.5">
          <span className="mt-0.5 inline-flex items-center justify-center w-9 h-9 rounded-full bg-white border border-sky-100 text-sky-500 shrink-0">
            <Globe className="w-[18px] h-[18px]" strokeWidth={2} />
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5">
              <h4 className="text-[14.5px] font-bold text-stone-900">글로벌 줌미팅</h4>
              {!isMember && <span className="inline-flex items-center gap-0.5 text-[10px] font-bold text-stone-500 bg-stone-100 px-1.5 py-0.5 rounded-full"><Lock className="w-2.5 h-2.5" />정회원 전용</span>}
            </div>
            <p className="text-[12.5px] text-stone-600 leading-relaxed mt-1">글로벌 한인 싱글들과 직접 얼굴을 보며 이야기해보세요.</p>
            {isMember && (
              <button
                onClick={() => navigate("/global-zoom")}
                className="mt-2.5 inline-flex items-center gap-1 text-[13px] font-bold text-sky-600 hover:text-sky-700"
                data-testid="rail-zoom-detail-btn"
              >
                줌미팅 자세히 보기 <ArrowRight className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ③ 혜택 2 — 모든 회원 프로필 보기 (핑크→블루 파스텔 히어로) */}
      <div className="mt-3 rounded-2xl p-4 bg-gradient-to-br from-pink-100 via-pink-50 to-sky-100 border border-sky-100 shadow-[0_2px_10px_-4px_rgba(147,197,253,0.4)]" data-testid="rail-members-cta">
        <div className="flex items-start gap-2.5">
          <span className="mt-0.5 inline-flex items-center justify-center w-10 h-10 rounded-full bg-white text-pink-400 shadow-sm shrink-0">
            <Users className="w-5 h-5" strokeWidth={2.2} />
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5 flex-wrap">
              <h4 className="text-[15.5px] font-bold text-stone-800">모든 회원 프로필 보기</h4>
              {!isMember && <span className="inline-flex items-center gap-0.5 text-[10px] font-bold text-sky-500 bg-white/70 px-1.5 py-0.5 rounded-full"><Lock className="w-2.5 h-2.5" />정회원 전용</span>}
            </div>
            <p className="text-[12.5px] text-stone-600 leading-relaxed mt-1">뉴커플링에 등록된 <b className="font-bold text-stone-700">모든 회원</b>의 프로필을 확인할 수 있습니다.</p>

            {/* 프로필 미리보기 썸네일 */}
            {thumbs.length > 0 && (
              <div className="flex items-center gap-1.5 mt-3" data-testid="rail-members-thumbs">
                {thumbs.map((m, i) => (
                  <div key={i} className="relative w-11 h-11 rounded-full overflow-hidden border-2 border-white shadow-sm bg-stone-200" style={{ marginLeft: i === 0 ? 0 : "-10px", zIndex: 10 - i }}>
                    <span className="absolute inset-0 flex items-center justify-center text-stone-400"><Users className="w-5 h-5" /></span>
                    {thumbUrl(m) && (
                      <img
                        src={thumbUrl(m)}
                        alt=""
                        onError={(e) => { e.currentTarget.style.display = "none"; }}
                        className={`relative w-full h-full object-cover ${isMember ? "" : "blur-[4px] scale-110"}`}
                      />
                    )}
                    {!isMember && <span className="absolute inset-0 flex items-center justify-center bg-stone-900/15"><Lock className="w-3.5 h-3.5 text-white" /></span>}
                  </div>
                ))}
                <span className="text-[12px] font-bold text-sky-500 ml-1.5">+ 수천 명 더보기</span>
              </div>
            )}

            {isMember ? (
              <Link
                to="/members"
                className="mt-3 w-full inline-flex items-center justify-center gap-1.5 py-2.5 rounded-full bg-gradient-to-r from-pink-400 to-sky-400 hover:from-pink-500 hover:to-sky-500 text-white text-[13.5px] font-bold shadow-sm transition-all"
                data-testid="rail-members-view-btn"
              >
                전체 회원 프로필 보기 <ArrowRight className="w-4 h-4" />
              </Link>
            ) : (
              <p className="text-[11.5px] text-stone-500 mt-2.5 leading-relaxed">🔒 정회원이 되면 모든 회원의 프로필을 볼 수 있어요.</p>
            )}
          </div>
        </div>
      </div>

      {/* ④ 비정회원 메인 CTA — 정회원 혜택 알아보기 */}
      {!isMember && (
        <button
          onClick={() => setShowBenefits(true)}
          className="mt-3.5 w-full inline-flex items-center justify-center gap-1.5 py-3 rounded-full bg-gradient-to-r from-pink-400 to-sky-400 hover:from-pink-500 hover:to-sky-500 text-white text-[14px] font-bold shadow-sm transition-all"
          data-testid="rail-premium-benefits-btn"
        >
          정회원 혜택 알아보기 <ArrowRight className="w-4 h-4" />
        </button>
      )}

      <PremiumBenefitsModal open={showBenefits} onClose={() => setShowBenefits(false)} />
    </section>
  );
}

/**
 * 우측 사이드바 — 광고 + 실시간 대화방 + 추천 + 이벤트 + (줌미팅·회원보기)
 */
export default function CommunityRightRail() {
  return (
    <aside className="w-full lg:w-80 shrink-0 space-y-3" data-testid="community-right-rail">
      {/* 페이지와 함께 스크롤 (내부 독립 스크롤/전체 sticky 없음). 순서: 정회원 전용 → 이벤트 → 신규회원 → 커뮤니티 → 광고 */}
      <PremiumZoomMembersCard />
      <UpcomingEvents />
      <RecommendedMembers />
      <WorryRoomCard />
      <CommunityNews />
      <CoupleHighlight />
      <AdBanner />
    </aside>
  );
}
